// The decoration, turned into sentences — P5-3 T10.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SAME RULE `estimate.js` OPENS WITH: THE ARITHMETIC IS NOT HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// Every number this module renders was computed by the trip service — the
// distance by OpenRouteService's matrix, the average by `utils/enrichment.py`,
// the converted figure by `utils/convert.py`. Nothing in this file divides an
// amount by a rate, and nothing in it decides whether a range is far enough
// away to need averages instead of a forecast.
//
// The half the service explicitly left here is the wording, and for enrichment
// the wording is most of the feature. §6:
//
//     absent-but-labeled when a provider is down — *"distance unavailable"* is
//     honest; a blank that might mean "close by" is not.
//
// A blank and a label are the same code path with a different string, and the
// string is the whole difference between the two.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 FOUR DISTANCE STATES, AND `missing` IS NOT `none`
// ═══════════════════════════════════════════════════════════════════════════
//
// HANDOFF's own model: `distanceState(ok | chosen | missing | none)`.
//
//   ok       a number
//   chosen   this card IS the stay everything else is measured from
//   missing  we tried and could not — amber, "distance unavailable"
//   none     nothing to try: no address on this card, or no stay chosen yet
//
// `missing` reports a provider. `none` reports the board. Rendering them the
// same would tell somebody their geocoder is down because they have three
// candidate stays and have not picked one — which is not a failure, and is not
// something the amber state should be claiming.

/** A date the service sent, as "21 Aug 2026". */
export function shortDate(iso) {
  if (typeof iso !== 'string' || iso === '') return null
  const parsed = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return null
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed)
}

/** "8.4 km from the stay". Rounded for reading, never for arithmetic.
 *
 * Under 10 km keeps one decimal, because 1.5 and 2 km are a different walk;
 * above it, a decimal on a road distance is precision the number does not
 * have — ORS measures a route, and which route depends on the day.
 */
export function kilometres(km) {
  if (typeof km !== 'number' || !Number.isFinite(km) || km < 0) return null
  return km < 10 ? `${Math.round(km * 10) / 10} km` : `${Math.round(km)} km`
}

/**
 * `{entryId: {distance}}` for `EntryCard`'s existing enrichment prop.
 *
 * 🔴 THE THREE-VALUE CONTRACT IS T4'S AND IT IS NOT CHANGED HERE. `EntryCard`
 * already distinguishes them, and `card-states.test.jsx` already drives all
 * three — that is what the T4 comment meant by *"so T10 has a seam rather than
 * a rewrite"*:
 *
 *     a string   render it
 *     null       render the amber "distance unavailable"
 *     undefined  render nothing at all
 *
 * So `none` maps to `undefined`. A card nobody typed an address into has no
 * distance field to report on, and an amber chip under it would be the board
 * reporting a failure that did not happen.
 */
export function distancesByEntry(section) {
  const out = new Map()
  const entries = Array.isArray(section?.entries) ? section.entries : []
  for (const item of entries) {
    if (!item || typeof item.entry_id !== 'string') continue
    if (item.state === 'ok') {
      const value = kilometres(item.km)
      out.set(item.entry_id, { distance: value === null ? null : `${value} from the stay` })
    } else if (item.state === 'chosen') {
      out.set(item.entry_id, { distance: 'the stay' })
    } else if (item.state === 'missing') {
      out.set(item.entry_id, { distance: null })
    }
    // `none` deliberately contributes nothing — see the header.
  }
  return out
}

// ── weather ────────────────────────────────────────────────────────────────

/** WMO-ish summary from what the service sends. Temperatures and rain only.
 *
 * The service does not send a weather CODE, deliberately: an icon set is a
 * design decision and HANDOFF's strip is drawn from temperature and a rain
 * flag. Everything below is derived from the two numbers already on screen.
 */
export function rainyDays(days) {
  return (Array.isArray(days) ? days : []).filter(
    (day) => typeof day?.rain_mm === 'number' && day.rain_mm >= 1,
  ).length
}

/**
 * The weather strip's values — HANDOFF screen 2, item 5.
 *
 * 🔴 `source` IS THE SENTENCE THE HANDOFF WRITES IN BOLD AROUND: *"ten-year
 * averages for that week, not a forecast — it's 8 months out · open-meteo,
 * fetched 16 Aug"*, followed by **"Never present as a forecast."**
 *
 * So the two modes get two different sentences, and the `normals` one says
 * *averages*, says *not a forecast*, and names how many years it actually
 * averaged. A section that says "ten-year averages" over six years of data is
 * the board claiming a completeness it does not have — which is the sentence
 * this whole product is arranged around.
 */
export function weatherStrip(section) {
  const state = section?.state
  if (state !== 'ok') {
    return {
      state: state === 'none' ? 'none' : 'unavailable',
      // §8: never blocks, never blank-that-looks-fine. The `none` reasons are
      // facts about the trip and are worded as such; `unavailable` is the
      // amber report of a provider.
      label:
        state === 'none'
          ? section?.reason === 'no-dates'
            ? 'weather once the dates are settled'
            : 'weather once there’s a place'
          : 'weather unavailable',
      days: [],
      source: null,
      summary: null,
    }
  }

  const days = Array.isArray(section.days) ? section.days : []
  const highs = days.map((day) => day.high_c).filter((value) => typeof value === 'number')
  const wet = rainyDays(days)
  const forecast = section.mode === 'forecast'

  const summary =
    highs.length === 0
      ? null
      : `${Math.round(Math.min(...highs))}–${Math.round(Math.max(...highs))}°C` +
        (wet === 0 ? ', dry' : wet === days.length ? ', rain every day' : `, rain on ${wet} of ${days.length}`)

  const fetched = shortDate(section.fetched_at)
  const source = forecast
    ? `forecast · open-meteo${fetched ? `, fetched ${fetched}` : ''}`
    : `${section.years_used ?? 0}-year averages for that week, not a forecast · open-meteo${
        fetched ? `, fetched ${fetched}` : ''
      }`

  return { state: 'ok', mode: section.mode, label: null, days, source, summary }
}

// ── conversion — the third honesty rule, now that it has a second half ─────

/**
 * The converted figure and its as-of date, or a labeled reason there is none.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE DATE IS NOT OPTIONAL, AND THAT IS TR7 RATHER THAN A PREFERENCE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *"Currency is per-amount, with a per-trip display currency; conversions
 * labeled with their as-of date."* Every shape this returns with a figure in
 * it also has `asOf` filled, and `src/test/trips/honesty.test.jsx` asserts the
 * two reach the screen together — because a converted figure without its date
 * is exactly the number this product spent nine slices refusing to print.
 *
 * §8's stale branch is the reason the date is worth having: *"last-known rates
 * used, **as-of date shown** — a visibly old date is honest."* When the feed
 * is down over a cached answer the service serves the old rates with their
 * ORIGINAL date, and this says `rates from 14 Aug 2026` rather than nothing.
 */
export function conversionFigure(section, money) {
  if (!section || section.state !== 'ok' || !section.converted) {
    return {
      state: 'absent',
      // 🔴 WORDED FROM THE REASON WE WERE GIVEN, AND NEVER FROM A GUESS — and
      // the case with NO reason is the one that matters most.
      //
      // Enrichment is a second request. For the first moment of every page
      // load, `section` is `undefined` and **nothing has failed**. Saying "no
      // rates today" there would be the board reporting a provider outage that
      // has not happened — the same mistake as rendering `distanceState: none`
      // as amber, one field over. So an absent section gets no sentence at
      // all, and only a section that TOLD us it failed gets one.
      label: absentLabel(section),
      figure: null,
      asOf: null,
      stale: false,
    }
  }

  const converted = section.converted
  return {
    state: 'ok',
    figure: money(converted.per_person, converted.target_currency),
    raw: converted.per_person,
    currency: converted.target_currency,
    asOf: shortDate(section.as_of),
    stale: Boolean(section.stale),
    components: Array.isArray(converted.components) ? converted.components : [],
    label: null,
  }
}

function absentLabel(section) {
  // Nothing was asked, or the answer has not landed. Not a failure, so not a
  // sentence about one.
  if (!section) return null

  const missing = section?.unconverted?.missing
  if (Array.isArray(missing) && missing.length > 0) {
    // Naming the currency is the whole value of refusing rather than
    // shortening: "we have no rate for JPY" is actionable; a total quietly
    // missing ¥48,000 is not even visible.
    return `no rate for ${[...missing].sort().join(' or ')}, so these aren’t added up`
  }
  if (section?.unconverted?.reason === 'nothing-priced') return null
  if (section.state === 'unavailable' || section?.unconverted?.reason === 'no-rates') {
    return 'no rates today, so these aren’t added up'
  }
  return null
}

/**
 * The sentence that goes on the honesty line where "kept apart" used to.
 *
 * 🔴 THE GUARD MOVED, IT DID NOT DISAPPEAR. Until this slice the board said
 * *"2 currencies, kept apart — nothing is converted yet"*, and that sentence
 * became false the moment conversion existed. Deleting it would have left two
 * figures side by side with nothing saying what the relationship between them
 * is — which is the state the original line was written to prevent.
 *
 * So there are now three sentences where there was one, and every one of them
 * still says out loud what the numbers are:
 *
 *   converted     "2 currencies · ≈ £633.48 all in at 21 Aug 2026 rates"
 *   refused       "2 currencies, kept apart — no rate for JPY…"
 *   single        nothing: one currency needs no sentence about currencies.
 */
export function currencyLine(figures, conversion) {
  const count = Array.isArray(figures) ? figures.length : 0
  if (count < 2) return null
  if (conversion?.state === 'ok' && conversion.figure) {
    const dated = conversion.asOf ? ` at ${conversion.asOf} rates` : ''
    const old = conversion.stale ? ' (latest we have)' : ''
    return `${count} currencies · ≈ ${conversion.figure} all in${dated}${old}`
  }
  const why = conversion?.label ? ` — ${conversion.label}` : ''
  return `${count} currencies, kept apart${why}`
}

/**
 * The conversion section as it applies to ONE destination.
 *
 * The service computes a converted figure per destination as well as one for
 * the whole trip, for the same reason it computes the roll-ups per destination
 * — the compare screen must not derive one, and deriving one means choosing a
 * rate in a renderer.
 *
 * 🔴 THE TOP-LEVEL `as_of` AND `stale` ARE CARRIED DOWN UNCHANGED, and that is
 * what makes a cross-column comparison possible at all: every column's figure
 * came from ONE rate set taken at ONE moment. If they could differ per column,
 * subtracting two of them would be subtracting two different days.
 */
export function conversionForDestination(section, destinationId) {
  if (!section) return undefined
  if (section.state !== 'ok') return section
  const rows = Array.isArray(section.by_destination) ? section.by_destination : []
  const row = rows.find((item) => item.destination_id === destinationId)
  if (!row) return { ...section, converted: null, unconverted: { reason: 'no-rates', missing: [] } }
  return {
    state: 'ok',
    target_currency: section.target_currency,
    as_of: section.as_of,
    stale: section.stale,
    converted: row.converted ?? null,
    unconverted: row.unconverted ?? null,
  }
}

// ── flights — a deep link, and TR5 says that is a ruling ───────────────────

/**
 * A prefilled flight search, or `null`. **Never a price.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 TR5, AND THE HANDOFF'S SENTENCE FOR IT: *"a scraped price is a lie with a
 * decimal point."*
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This function builds a URL out of values the page already has and returns a
 * string. It performs no request, in this module or anywhere else — there is
 * no flight host in `trips/utils/providers.py`'s allow-list, no flight
 * endpoint in the service, and nothing in this file that could fetch one.
 * `src/test/trips/enrichment.test.js` asserts all three.
 *
 * ⚠️ NO ORIGIN IS GUESSED. The site knows where the trip is going and has no
 * idea where anybody is flying from — six people is potentially six airports.
 * A prefilled "from London" would be wrong for most of the crew and would look
 * authoritative, so the link carries the destination and the dates and lets
 * the search ask.
 */
export function flightSearchUrl({ place, start, end } = {}) {
  const to = typeof place === 'string' ? place.trim() : ''
  if (to === '' || !start || !end) return null
  const query = `Flights to ${to} on ${start} through ${end}`
  return `https://www.google.com/travel/flights?q=${encodeURIComponent(query)}`
}
